import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import Button from "../../Components/Button";
import Card from "../../Components/Card";
import SendBulkEmailModal from "../../Components/SendBulkEmailModal";
import TextField from "../../Components/TextField";
import { Typography } from "../../Components/Typography";
import { getAllEventRegistrations, getEventRegistrationById, updateEventRegistration, deleteEventRegistration } from "../../Utils/apiServices";
import toast from "../../Utils/toastManager";

const Volunteers = () => {
  const navigate = useNavigate();
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [volunteerEmails, setVolunteerEmails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    status: "",
    current_occupation: "",
    admin_review_notes: "",
  });

  const fetchVolunteers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllEventRegistrations({ role: "volunteer" });
      if (response.success && response?.data?.data) {
        const volunteerData = Array.isArray(response.data.data) ? response.data.data : [];
        setVolunteers(volunteerData);
        // Extract emails for bulk email
        const emails = volunteerData
          .map((volunteer) => volunteer.email)
          .filter((email) => email && email.trim() !== "");
        setVolunteerEmails(emails);
      }
    } catch (error) {
      console.error("Error fetching volunteers:", error);
      setVolunteers([]);
      setVolunteerEmails([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVolunteers();
  }, [fetchVolunteers]);

  const getDisplayName = (volunteer) => {
    // Use name field from volunteer data
    if (volunteer.name) {
      return volunteer.name;
    }
    // Fallback to email if name is not available
    if (volunteer.email) {
      return volunteer.email;
    }
    return "Unknown";
  };

  const formatStatus = (status) => {
    // Map status values to display format
    const statusMap = {
      registered: "Applied",
      active: "Active",
      rejected: "Rejected",
      approved: "Active",
    };
    return statusMap[status] || status || "Applied";
  };

  const getStatusColor = (status) => {
    const formattedStatus = formatStatus(status);
    if (formattedStatus === "Active") {
      return "bg-green-100 text-green-800";
    } else if (formattedStatus === "Rejected") {
      return "bg-red-100 text-red-800";
    }
    return "bg-yellow-100 text-yellow-800";
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEdit = async (volunteer) => {
    try {
      // Fetch full volunteer details if needed
      const response = await getEventRegistrationById(volunteer.id);
      if (response.success && response.data?.data) {
        const volunteerData = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
        setEditingId(volunteer.id);
        setFormData({
          status: volunteerData.status || "",
          current_occupation: volunteerData.current_occupation || "",
          admin_review_notes: volunteerData.admin_review_notes || "",
        });
      } else {
        // Fallback to the volunteer data we already have
        setEditingId(volunteer.id);
        setFormData({
          status: volunteer.status || "",
          current_occupation: volunteer.current_occupation || "",
          admin_review_notes: volunteer.admin_review_notes || "",
        });
      }
    } catch (error) {
      console.error("Error fetching volunteer details:", error);
      // Fallback to the volunteer data we already have
      setEditingId(volunteer.id);
      setFormData({
        status: volunteer.status || "",
        current_occupation: volunteer.current_occupation || "",
        admin_review_notes: volunteer.admin_review_notes || "",
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({
      status: "",
      current_occupation: "",
      admin_review_notes: "",
    });
  };

  const handleSave = async (volunteerId) => {
    try {
      const response = await updateEventRegistration(volunteerId, formData);
      if (response.success) {
        await fetchVolunteers();
        handleCancelEdit();
      } else {
        toast.error("Failed to update volunteer: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error saving volunteer:", error);
      toast.error("An error occurred while saving the volunteer");
    }
  };

  const handleDelete = async (volunteerId) => {
    if (!window.confirm("Are you sure you want to delete this volunteer registration?")) {
      return;
    }

    try {
      const response = await deleteEventRegistration(volunteerId);
      if (response.success) {
        await fetchVolunteers();
      } else {
        toast.error("Failed to delete volunteer: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error deleting volunteer:", error);
      toast.error("An error occurred while deleting the volunteer");
    }
  };

  // Filter volunteers based on search term
  const filteredVolunteers = useMemo(() => {
    if (!searchTerm.trim()) return volunteers;
    
    const searchLower = searchTerm.toLowerCase();
    return volunteers.filter((volunteer) => {
      const name = (volunteer.name || "").toLowerCase();
      const email = (volunteer.email || "").toLowerCase();
      const occupation = (volunteer.current_occupation || "").toLowerCase();
      const eventTitle = (volunteer.event?.title || "").toLowerCase();
      
      return (
        name.includes(searchLower) ||
        email.includes(searchLower) ||
        occupation.includes(searchLower) ||
        eventTitle.includes(searchLower)
      );
    });
  }, [volunteers, searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Typography as="p" className="text-tertiary-600">
          Loading Volunteers...
        </Typography>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <Typography as="h2" className="text-2xl font-semibold text-primary-900">
            Volunteers
          </Typography>
          <Button
            onClick={() => setShowBulkEmailModal(true)}
            startIcon={<Icon icon="lucide:mail" className="h-4 w-4" />}
          >
            Send Bulk Email
          </Button>
        </div>
      </div>

      <Card 
        className="border border-slate-200 overflow-hidden"
        header={
          <div className="flex items-center justify-end w-full">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-48 px-3 py-1.5 pl-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
              />
              <Icon
                icon="lucide:search"
                className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400"
              />
            </div>
          </div>
        }
      >
        {volunteers.length === 0 ? (
          <div className="p-6 text-center">
            <Typography as="p" className="text-tertiary-600">
              No volunteers found.
            </Typography>
          </div>
        ) : (
          <div className="overflow-x-auto">
              <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    Occupation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    Event Applied For
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredVolunteers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center">
                      <Typography as="p" className="text-tertiary-600">
                        No volunteers found matching your search.
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  filteredVolunteers.map((volunteer) => (
                  <React.Fragment key={volunteer.id}>
                    {editingId === volunteer.id ? (
                      <tr className="bg-slate-50">
                        <td colSpan="5" className="px-6 py-4">
                          <div className="space-y-4">
                            <Typography
                              as="h3"
                              className="text-base font-semibold text-primary-900 mb-4"
                            >
                              Edit Volunteer
                            </Typography>
                            <div className="grid grid-cols-1 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  Status
                                </label>
                                <select
                                  name="status"
                                  value={formData.status || ""}
                                  onChange={(e) =>
                                    handleInputChange("status", e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-slate-900 text-sm"
                                >
                                  <option value="registered">Applied</option>
                                  <option value="approved">Approved</option>
                                  <option value="rejected">Rejected</option>
                                </select>
                              </div>
                              <TextField
                                name="current_occupation"
                                label="Current Occupation"
                                type="text"
                                value={formData.current_occupation}
                                onChange={(e) =>
                                  handleInputChange("current_occupation", e.target.value)
                                }
                                placeholder="Enter current occupation"
                              />
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  Admin Review Notes
                                </label>
                                <textarea
                                  name="admin_review_notes"
                                  value={formData.admin_review_notes}
                                  onChange={(e) =>
                                    handleInputChange("admin_review_notes", e.target.value)
                                  }
                                  placeholder="Enter admin review notes"
                                  rows={4}
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                            <div className="flex items-center justify-end gap-3">
                              <Button variant="secondary" onClick={handleCancelEdit}>
                                Cancel
                              </Button>
                              <Button onClick={() => handleSave(volunteer.id)}>
                                Save
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap align-middle">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <Icon icon="lucide:user" className="h-5 w-5 text-slate-500" />
                            </div>
                            <button
                              onClick={() => navigate(`/event-registrations/${volunteer.id}`)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline cursor-pointer text-left"
                            >
                              {getDisplayName(volunteer)}
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap align-middle">
                          <Typography as="span" className="text-sm text-tertiary-600">
                            {volunteer.current_occupation || "-"}
                          </Typography>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap align-middle">
                          <Typography as="span" className="text-sm text-tertiary-600">
                            {volunteer.event?.title || "-"}
                          </Typography>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap align-middle">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                              volunteer.status
                            )}`}
                          >
                            {formatStatus(volunteer.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-left align-middle">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleEdit(volunteer)}
                              startIcon={<Icon icon="lucide:edit" className="h-4 w-4" />}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleDelete(volunteer.id)}
                              startIcon={
                                <Icon icon="lucide:trash-2" className="h-4 w-4" />
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <SendBulkEmailModal
        isOpen={showBulkEmailModal}
        onClose={() => setShowBulkEmailModal(false)}
        emails={volunteerEmails}
      />
    </>
  );
};

export default Volunteers;

